import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';

import { CookieBanner } from '@/components/cookie-banner';
import { AppShell } from '@/components/layout/app-shell';
import { SkipToMain } from '@/components/skip-to-main';
import { AcessoNegadoPage } from '@/features/auth/acesso-negado-page';
import { LoginPage } from '@/features/auth/login-page';
import { SignupPage } from '@/features/auth/signup-page';
import { DashboardPage } from '@/features/dashboard/dashboard-page';
import { OnboardingTenantPage } from '@/features/onboarding/onboarding-tenant-page';
import { CadastrarLavaJatoPage } from '@/features/self-serve/cadastrar-lava-jato-page';
import { AuthProvider, RequireAdmin } from '@/lib/auth-context';
import { TenantProvider } from '@/lib/tenant-context';

// Telas pesadas / pouco usadas: lazy load
const AssinaturasListPage = lazy(() =>
  import('@/features/assinaturas/assinaturas-list-page').then((m) => ({
    default: m.AssinaturasListPage,
  })),
);
const InadimplenciasPage = lazy(() =>
  import('@/features/assinaturas/inadimplencias-page').then((m) => ({
    default: m.InadimplenciasPage,
  })),
);
const PagamentosPage = lazy(() =>
  import('@/features/assinaturas/pagamentos-page').then((m) => ({ default: m.PagamentosPage })),
);
const ChargebacksPage = lazy(() =>
  import('@/features/assinaturas/chargebacks-page').then((m) => ({ default: m.ChargebacksPage })),
);
const WebhooksPage = lazy(() =>
  import('@/features/extras/webhooks-page').then((m) => ({ default: m.WebhooksPage })),
);
const ClienteDetailPage = lazy(() =>
  import('@/features/clientes/cliente-detail-page').then((m) => ({ default: m.ClienteDetailPage })),
);
const ClientesListPage = lazy(() =>
  import('@/features/clientes/clientes-list-page').then((m) => ({ default: m.ClientesListPage })),
);
const AlertasListPage = lazy(() =>
  import('@/features/extras/alertas-list-page').then((m) => ({ default: m.AlertasListPage })),
);
const SaudePage = lazy(() =>
  import('@/features/extras/saude-page').then((m) => ({ default: m.SaudePage })),
);
const FeaturesPage = lazy(() =>
  import('@/features/extras/features-page').then((m) => ({ default: m.FeaturesPage })),
);
const ConfiguracoesPage = lazy(() =>
  import('@/features/extras/configuracoes-page').then((m) => ({ default: m.ConfiguracoesPage })),
);
const FidelidadeAdminPage = lazy(() =>
  import('@/features/fidelidade/fidelidade-admin-page').then((m) => ({
    default: m.FidelidadeAdminPage,
  })),
);
const ModulosPage = lazy(() =>
  import('@/features/modulos/modulos-page').then((m) => ({ default: m.ModulosPage })),
);
const AutoSystemPage = lazy(() =>
  import('@/features/autosystem/autosystem-page').then((m) => ({ default: m.AutoSystemPage })),
);
const ParceirosResgatePage = lazy(() =>
  import('@/features/parceiros-resgate/parceiros-resgate-page').then((m) => ({
    default: m.ParceirosResgatePage,
  })),
);
const ParceirosPage = lazy(() =>
  import('@/features/fidelidade/parceiros-page').then((m) => ({ default: m.ParceirosPage })),
);
const MetricasPage = lazy(() =>
  import('@/features/extras/metricas-page').then((m) => ({ default: m.MetricasPage })),
);
const AvaliacoesPage = lazy(() =>
  import('@/features/extras/avaliacoes-page').then((m) => ({ default: m.AvaliacoesPage })),
);
const DiagnosticoPage = lazy(() =>
  import('@/features/extras/diagnostico-page').then((m) => ({ default: m.DiagnosticoPage })),
);
const AvulsoListPage = lazy(() =>
  import('@/features/extras/avulso-list-page').then((m) => ({ default: m.AvulsoListPage })),
);
const PrecosAvulsoPage = lazy(() =>
  import('@/features/extras/precos-avulso-page').then((m) => ({ default: m.PrecosAvulsoPage })),
);
const BannersPage = lazy(() =>
  import('@/features/extras/banners-page').then((m) => ({ default: m.BannersPage })),
);
const AntiFraudePage = lazy(() =>
  import('@/features/extras/anti-fraude-page').then((m) => ({ default: m.AntiFraudePage })),
);
const FraudePage = lazy(() =>
  import('@/features/fraude/fraude-page').then((m) => ({ default: m.FraudePage })),
);
const CorporativoPage = lazy(() =>
  import('@/features/extras/corporativo-page').then((m) => ({ default: m.CorporativoPage })),
);
const HardwarePage = lazy(() =>
  import('@/features/extras/hardware-page').then((m) => ({ default: m.HardwarePage })),
);
const IndicacoesPage = lazy(() =>
  import('@/features/extras/indicacoes-page').then((m) => ({ default: m.IndicacoesPage })),
);
const MultiLoginPage = lazy(() =>
  import('@/features/extras/multi-login-page').then((m) => ({ default: m.MultiLoginPage })),
);
const PainelOperacionalPage = lazy(() =>
  import('@/features/extras/painel-operacional-page').then((m) => ({
    default: m.PainelOperacionalPage,
  })),
);
const PausasPage = lazy(() =>
  import('@/features/extras/pausas-page').then((m) => ({ default: m.PausasPage })),
);
const SimularHardwarePage = lazy(() =>
  import('@/features/extras/simular-hardware-page').then((m) => ({
    default: m.SimularHardwarePage,
  })),
);
const LogsAuditoriaPage = lazy(() =>
  import('@/features/lgpd/logs-auditoria-page').then((m) => ({ default: m.LogsAuditoriaPage })),
);
const SolicitacoesPage = lazy(() =>
  import('@/features/lgpd/solicitacoes-page').then((m) => ({ default: m.SolicitacoesPage })),
);
const DoisFatoresPage = lazy(() =>
  import('@/features/seguranca/dois-fatores-page').then((m) => ({ default: m.DoisFatoresPage })),
);
const SuperAdminPage = lazy(() =>
  import('@/features/super-admin/super-admin-page').then((m) => ({ default: m.SuperAdminPage })),
);
const MensagensEnviadasPage = lazy(() =>
  import('@/features/mensagens/mensagens-enviadas-page').then((m) => ({
    default: m.MensagensEnviadasPage,
  })),
);
const UazapiInstanciasPage = lazy(() =>
  import('@/features/mensagens/uazapi-instancias-page').then((m) => ({
    default: m.UazapiInstanciasPage,
  })),
);
const TemplatesMensagemPage = lazy(() =>
  import('@/features/mensagens/templates-page').then((m) => ({ default: m.TemplatesMensagemPage })),
);
const BloqueiosPage = lazy(() =>
  import('@/features/operacao/bloqueios-page').then((m) => ({ default: m.BloqueiosPage })),
);
const HistoricoLavagensPage = lazy(() =>
  import('@/features/operacao/historico-lavagens-page').then((m) => ({
    default: m.HistoricoLavagensPage,
  })),
);
const MaquinasListPage = lazy(() =>
  import('@/features/operacao/maquinas-list-page').then((m) => ({ default: m.MaquinasListPage })),
);
const OperacaoAoVivoPage = lazy(() =>
  import('@/features/operacao/operacao-ao-vivo-page').then((m) => ({
    default: m.OperacaoAoVivoPage,
  })),
);
const UnidadesListPage = lazy(() =>
  import('@/features/operacao/unidades-list-page').then((m) => ({ default: m.UnidadesListPage })),
);
const PlanoDetailPage = lazy(() =>
  import('@/features/planos/plano-detail-page').then((m) => ({ default: m.PlanoDetailPage })),
);
const PlanosListPage = lazy(() =>
  import('@/features/planos/planos-list-page').then((m) => ({ default: m.PlanosListPage })),
);
const PromocoesPage = lazy(() =>
  import('@/features/planos/promocoes-page').then((m) => ({ default: m.PromocoesPage })),
);
const VeiculoDetailPage = lazy(() =>
  import('@/features/veiculos/veiculo-detail-page').then((m) => ({ default: m.VeiculoDetailPage })),
);
const VeiculosListPage = lazy(() =>
  import('@/features/veiculos/veiculos-list-page').then((m) => ({ default: m.VeiculosListPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoader() {
  return (
    <div className="text-muted-foreground flex h-screen items-center justify-center">
      Carregando...
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <AuthProvider>
          <BrowserRouter>
            <SkipToMain />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/cadastro" element={<SignupPage />} />
              <Route path="/cadastrar-lava-jato" element={<CadastrarLavaJatoPage />} />
              <Route path="/onboarding" element={<OnboardingTenantPage />} />
              <Route path="/acesso-negado" element={<AcessoNegadoPage />} />

              <Route
                element={
                  <RequireAdmin>
                    <AppShell />
                  </RequireAdmin>
                }
              >
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route
                  path="/clientes"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ClientesListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/clientes/:seqCliente"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ClienteDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/veiculos"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <VeiculosListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/veiculos/:seqVeiculo"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <VeiculoDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/planos"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PlanosListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/planos/:seqPlano"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PlanoDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/promocoes"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PromocoesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/assinaturas"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AssinaturasListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/pagamentos"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PagamentosPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/inadimplencias"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <InadimplenciasPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/chargebacks"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ChargebacksPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/webhooks"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <WebhooksPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/operacao"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <OperacaoAoVivoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/unidades"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <UnidadesListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/maquinas"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <MaquinasListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/lavagens"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <HistoricoLavagensPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/bloqueios"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <BloqueiosPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/avulso"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AvulsoListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/precos-avulso"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PrecosAvulsoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/banners"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <BannersPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/anti-fraude"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AntiFraudePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/fraude"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FraudePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/corporativo"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <CorporativoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/multi-login"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <MultiLoginPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/pausas"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PausasPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/indicacoes"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <IndicacoesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/painel-op"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PainelOperacionalPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/alertas"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AlertasListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/saude"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SaudePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/features"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FeaturesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/metricas"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <MetricasPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/avaliacoes"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AvaliacoesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/diagnostico"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <DiagnosticoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/hardware"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <HardwarePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/hardware/simular"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SimularHardwarePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/mensagens/templates"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <TemplatesMensagemPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/mensagens/enviadas"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <MensagensEnviadasPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/mensagens/uazapi"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <UazapiInstanciasPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/lgpd/solicitacoes"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SolicitacoesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/lgpd/logs"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <LogsAuditoriaPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/seguranca/2fa"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <DoisFatoresPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/super-admin"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SuperAdminPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/configuracoes"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ConfiguracoesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/fidelidade-admin"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FidelidadeAdminPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/modulos"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ModulosPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/autosystem"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AutoSystemPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/parceiros-resgate"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ParceirosResgatePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/parceiros"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ParceirosPage />
                    </Suspense>
                  }
                />
              </Route>

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          <CookieBanner />
        </AuthProvider>
      </TenantProvider>
    </QueryClientProvider>
  );
}
